import { useTranslation } from "react-i18next";
import { Bug } from "lucide-react";
import { InfoCard } from "@/components/result/InfoCard";

/**
 * Common pests / diseases card for the Result page.
 *
 * Renders a chemical-free, natural-remedy focused list — every pest entry has
 * up to three sub-fields (name, symptoms, natural_remedy). Returns null when
 * the identification has no pests so the parent can `<PestIssuesCard />`
 * unconditionally without a guard.
 */
export const PestIssuesCard = ({ pests }) => {
  const { t } = useTranslation();
  if (!Array.isArray(pests) || pests.length === 0) return null;
  return (
    <InfoCard
      title={t("result.pests")}
      icon={<Bug className="w-4 h-4" strokeWidth={1.5} />}
      testId="card-pests"
    >
      <p className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist mb-3">
        {t("result.pestsSubtitle")}
      </p>
      <div className="space-y-3">
        {pests.map((pest, i) => (
          <div key={`pest-${i}`} data-testid={`pest-${i}`} className="rounded-2xl bg-parchment/60 p-3">
            {pest.name && (
              <p className="font-medium text-leaf-ink text-sm" data-testid={`pest-${i}-name`}>{pest.name}</p>
            )}
            {pest.symptoms && (
              <p className="mt-1 text-[13px] text-leaf-mist" data-testid={`pest-${i}-symptoms`}>
                <span className="font-medium text-leaf-ink/80">{t("result.pestsSymptoms")}</span> {pest.symptoms}
              </p>
            )}
            {pest.natural_remedy && (
              <p className="mt-1 text-[13px] text-leaf" data-testid={`pest-${i}-remedy`}>
                <span className="font-medium">{t("result.pestsNatural")}</span> {pest.natural_remedy}
              </p>
            )}
          </div>
        ))}
      </div>
    </InfoCard>
  );
};

export default PestIssuesCard;
